// GuidanceCards.js - Side-by-side abbreviated Remediation + Hardening cards for V3 (PRD-43)
// Shows top 5 items as title + priority badge. Clicking the card (or any row)
// invokes a callback so the parent can deep-link into the full tab view.

import React from 'react';
import './GuidanceCards.css';

const MAX_ITEMS = 5;

function priorityTone(priority) {
    if (!priority) return 'info';
    const p = String(priority).toLowerCase();
    if (p.includes('critical') || p.includes('p0')) return 'critical';
    if (p.includes('high') || p.includes('p1')) return 'high';
    if (p.includes('medium') || p.includes('p2')) return 'medium';
    if (p.includes('low') || p.includes('p3')) return 'low';
    return 'info';
}

/**
 * Normalize a defensive recommendations payload into a flat list of
 * { title, category } entries.
 *
 * Per PRD-43 §"V3 Data Mapping Gaps" #3: the real shape is a flat ARRAY of
 * objects. N8N Claude schema says `{layer, control, priority}` but production
 * AssetDetails.js reads `{category, recommendation, priority, effort}` —
 * Claude generates inconsistent shapes so we accept both. Items may also be
 * plain strings. Object-keyed-by-category (V2 assumption) is NOT a real shape.
 */
function flattenHardening(defensive) {
    if (!defensive || !Array.isArray(defensive)) return [];
    return defensive
        .map((item) => {
            if (typeof item === 'string') {
                return { title: item, category: 'General' };
            }
            if (!item || typeof item !== 'object') return null;
            return {
                title: item.recommendation || item.control || item.text || item.description || '',
                category: item.category || item.layer || 'General'
            };
        })
        .filter((x) => x && x.title);
}

function RemediationAbbrev({ recommendations, onOpen, onItemClick }) {
    const list = Array.isArray(recommendations) ? recommendations : [];
    const items = list.slice(0, MAX_ITEMS);
    const hidden = Math.max(0, list.length - items.length);

    return (
        <div className="guidance-card guidance-card--remediation">
            <header className="guidance-card__header" onClick={() => onOpen && onOpen()} role="button" tabIndex={0}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onOpen) { e.preventDefault(); onOpen(); } }}>
                <div className="guidance-card__title-wrap">
                    <h3 className="guidance-card__title">Remediation Priorities</h3>
                    <p className="guidance-card__subtitle">Ranked by exploitability and impact</p>
                </div>
                {list.length > 0 && (
                    <span className="guidance-card__count guidance-card__count--critical">{list.length}</span>
                )}
            </header>
            {items.length === 0 ? (
                <div className="guidance-card__empty">No remediation priorities available.</div>
            ) : (
                <ul className="guidance-card__list">
                    {items.map((rec, i) => {
                        // Per PRD-43 §"V3 Data Mapping Gaps" #2: `vulnerability` is the
                        // canonical title field; `title`/`finding` don't exist in real blobs.
                        const title = rec.vulnerability || rec.title || `Finding ${i + 1}`;
                        const priority = rec.priority || null;
                        return (
                            <li
                                key={i}
                                className="guidance-card__item"
                                onClick={() => onItemClick ? onItemClick(rec, i) : (onOpen && onOpen(i))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        (onItemClick ? onItemClick(rec, i) : (onOpen && onOpen(i)));
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                {priority && (
                                    <span className={`guidance-card__tag guidance-card__tag--${priorityTone(priority)}`}>
                                        {priority}
                                    </span>
                                )}
                                <span className="guidance-card__item-title" title={title}>{title}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
            {(hidden > 0 || list.length > 0) && (
                <footer className="guidance-card__footer">
                    <button type="button" className="guidance-card__link" onClick={() => onOpen && onOpen()}>
                        {hidden > 0 ? `View all ${list.length} remediations →` : 'View full details →'}
                    </button>
                </footer>
            )}
        </div>
    );
}

function HardeningAbbrev({ defensive, onOpen, onItemClick }) {
    const list = flattenHardening(defensive);
    const items = list.slice(0, MAX_ITEMS);
    const hidden = Math.max(0, list.length - items.length);

    return (
        <div className="guidance-card guidance-card--hardening">
            <header className="guidance-card__header" onClick={() => onOpen && onOpen()} role="button" tabIndex={0}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onOpen) { e.preventDefault(); onOpen(); } }}>
                <div className="guidance-card__title-wrap">
                    <h3 className="guidance-card__title">Hardening Recommendations</h3>
                    <p className="guidance-card__subtitle">Proactive security controls to implement</p>
                </div>
                {list.length > 0 && (
                    <span className="guidance-card__count guidance-card__count--info">{list.length}</span>
                )}
            </header>
            {items.length === 0 ? (
                <div className="guidance-card__empty">No hardening recommendations available.</div>
            ) : (
                <ul className="guidance-card__list">
                    {items.map((rec, i) => (
                        <li
                            key={i}
                            className="guidance-card__item"
                            onClick={() => onItemClick ? onItemClick(rec, i) : (onOpen && onOpen(i))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    (onItemClick ? onItemClick(rec, i) : (onOpen && onOpen(i)));
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <span className="guidance-card__tag guidance-card__tag--info">
                                {rec.category}
                            </span>
                            <span className="guidance-card__item-title" title={rec.title}>{rec.title}</span>
                        </li>
                    ))}
                </ul>
            )}
            {(hidden > 0 || list.length > 0) && (
                <footer className="guidance-card__footer">
                    <button type="button" className="guidance-card__link" onClick={() => onOpen && onOpen()}>
                        {hidden > 0 ? `View all ${list.length} recommendations →` : 'View full details →'}
                    </button>
                </footer>
            )}
        </div>
    );
}

/**
 * Side-by-side abbreviated Remediation + Hardening cards.
 *
 * @param {object} props
 * @param {Array}  props.recommendations - aiAnalysis.prioritizedRecommendations
 * @param {object|Array} props.defensive - aiAnalysis.defensiveRecommendations
 * @param {function} props.onOpenRemediation - called when user clicks remediation card/row
 * @param {function} props.onOpenHardening   - called when user clicks hardening card/row
 */
function GuidanceCards({ recommendations, defensive, onOpenRemediation, onOpenHardening }) {
    return (
        <div className="guidance-cards">
            <RemediationAbbrev recommendations={recommendations} onOpen={onOpenRemediation} />
            <HardeningAbbrev defensive={defensive} onOpen={onOpenHardening} />
        </div>
    );
}

export default GuidanceCards;
